import React, { useState, useMemo } from "react";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { useTranslation } from "src/context/Translations/context";
import { useForm } from "react-hook-form";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCServicesApi, RCCTooltip } from "react-common-components";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import { standardErrorMessages } from "src/utils/errors";
import Title from "src/components/Title";
import LovRiskClassification from "src/components/Lov/RiskClassification";
import LovUsers from "src/components/Lov/Users";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import Select from "src/components/Select";
import LovProjects from "src/components/Lov/Projects";
import Loader from "src/components/Loader";
import { getLocalStorage, setLocalStorage } from "src/utils/localStorage";
import { useAuth } from "src/context/Auth/context";
import LovProcess from "src/components/Lov/Process";
import AppIcons from "src/components/AppIcons";
import { useLocation } from "react-router-dom";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import stringToDate from "src/utils/stringToDate";
import formatDate from "src/utils/formatDate";
import RisksMatrixComponent from "./components/Matrix";
import ListOfRisks from "../Panel/components/listOfRisks";
import { CheckImpact, CheckProbability, CheckQuadrant, RisksSituation, IRisks } from "../Panel/interface";
import { IFilters } from "./interface";


const RisksMatrix: React.FC = () => { 

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();

	const {user} = useAuth();

	const [layoutList, setLayoutList] = useState<"card" | "list">(getLocalStorage(`RisksScreenLayoutList-${user.efa005Id}`) === "card" ? "card" : "list");


	const impactDictonary = [
		{ value: CheckImpact.VERY_HIGH, label:  translate("Muito alto")},
		{ value: CheckImpact.HIGH, label:  translate("Alto")},
		{ value: CheckImpact.MODERATE, label:  translate("Moderado")},
		{ value: CheckImpact.LOW, label: translate("Baixo")}, 
		{ value: CheckImpact.VERY_LOW, label: translate("Muito baixo")} 
	];

	const probabilityDictonary = [
		{ value: CheckProbability.VERY_HIGH, label:  translate("Muito alto")},
		{ value: CheckProbability.HIGH, label:  translate("Alto")},
		{ value: CheckProbability.MODERATE, label:  translate("Moderado")},
		{ value: CheckProbability.LOW, label: translate("Baixo")}, 
		{ value: CheckProbability.VERY_LOW, label: translate("Muito baixo")} 
	];
	
	const [list, setList] = useState<IRisks[]>([]);
	const [listFiltered, setListFiltered] = useState<IRisks[]>([]);
	const [selected, setSelected] = useState(false);
	const [selectedProbability, setSelectedProbability] = useState<CheckProbability | undefined>();
	const [selectedImpact, setSelectedImpact] = useState<CheckImpact | undefined>();
	const [selectedQuadrant, setSelectedQuadrant] = useState<CheckQuadrant | undefined>();

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const {handleSubmit : handleSubmitFilters, control:controlFilters, register : registerFilters, reset:resetFilters, watch: watchFilters,setValue:setValueFilters } = useForm();
	const [filters, setFilters] = useState<IFilters>({ 
		filter: "", 
		active: objQueryParams?.active ?? "T", 
		dateStart: objQueryParams?.dateStart ? stringToDate(objQueryParams?.dateStart) : null,
		dateEnd: objQueryParams?.dateEnd ? stringToDate(objQueryParams?.dateEnd) : null, 
		tags: objQueryParams?.tags ?? "",
		classification: objQueryParams?.classification ?? "",
		situation: objQueryParams?.situation ?? undefined
	});

	const submitFilters = (data: any) => {
		setFilters(() => {	
			const newState = {} as any;
			newState.filter = data.filter;
			newState.rangeStart = data.rangeStart ?  data.rangeStart : "";
			newState.rangeEnd= data.rangeEnd ?  data.rangeEnd : "";
			newState.ria001Id=  data.ria001Id ? data.ria001Id : "";
			newState.situation=  data.situation && data.situation.toString() !== "T" ? objToValue(data.situation) : "";
			newState.process = data.process ?  objToValue(data.process) : "";
			newState.projects = data.projects ?  objToValue(data.projects) : "";
			newState.tags = data.tags ? data.tags : "";
			newState.responsable = data.responsable ?  objToValue(data.responsable) : "";
			newState.classification = data.classification ? objToValue(data.classification) : "";
			newState.dateStart = data.dateStart ?? null;
			newState.dateEnd = data.dateEnd ?? null;
			return newState;
		});
	};
	
	const objToValue = (obj: any) => {
		if(typeof obj === "object")
			return obj.value;
		if(typeof obj === "string")
			return parseInt(obj, 10) ;
		return obj;
	};

	const filterListBasedOnProps = (probability?: CheckProbability, impact? : CheckImpact, quadrant?: CheckQuadrant, listNew?: IRisks[]) => {

		if(!probability && !impact && !quadrant){
			setSelected(false);
		}else{
			setSelected(true);
		}

		setSelectedProbability(probability ?? undefined);
		setSelectedImpact(impact ?? undefined);
		setSelectedQuadrant(quadrant ?? undefined);
		

		let dataFiltered;

		if(listNew){
			setList(listNew);
			dataFiltered = listNew.filter((item : IRisks) => (item.rib001Probabilidade === probability) && (item.rib001Impacto === impact) && (item.rib001Quadrante === quadrant));
		}else if(!probability && !impact && !quadrant)
			dataFiltered = list;
		else
			dataFiltered = list.filter((item : IRisks) => (item.rib001Probabilidade === probability) && (item.rib001Impacto === impact) && (item.rib001Quadrante === quadrant));

		setListFiltered(dataFiltered);
		
	};

	const { errorFetching,  loading } = useEffectFetchData<IRisks[]> (
		"Risks/List", 
		{ 
			...filters,
			situation: filters.situation && (filters.situation.toString() !== RisksSituation.ALL.toString()) ? filters.situation : "",
			dateStart: filters.dateStart ? formatDate(filters.dateStart) : "",
			dateEnd: filters.dateEnd ? formatDate(filters.dateEnd) : ""
		 },
		 ({ data }) => {
			setList(data);
			setSelected(false);
		 },
		 [filters]
	);



	const onCallbackDelete = (rib001Id: number) => {
		const listnew = list.filter((x) => x.rib001Id !== rib001Id);
		if(selectedProbability && selectedImpact && selectedQuadrant)
			filterListBasedOnProps(selectedProbability, selectedImpact, selectedQuadrant, listnew);
	};

	const onCallbackUpdate = async (id: number) => {
		try{

			const response = await RCCServicesApi.getRequest({
				method: "Risks/InfosOfRiskForList",
				urlParameters: {
					id
				}
			});

			const risk = response.data as IRisks;

			const listnew = list.map((glist) => {
				if(glist.rib001Id === id)
					return risk;
				return glist;
			});

			if(selectedProbability && selectedImpact && selectedQuadrant)
				filterListBasedOnProps(selectedProbability, selectedImpact, selectedQuadrant, listnew);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar risco, favor atualizar a pagina"), addToast);
		}
	};


	const Matrix = useMemo(() => (
		<>
			{errorFetching ? (
				<RCCAlert 
					color="red"
					title={translate("Atenção")}
					description={translate("Erro ao carregar dados")}
				/>
			): 
				<>
					{loading ? 
						<Loader /> : 
						<>
							<RisksMatrixComponent data={list} handleClickCell={filterListBasedOnProps} />
						</>
					}
				</>
			}
		</>
	), [list, loading]);

	const situationOptions = [
		{
			value: RisksSituation.OPEN.toString(),
			label: translate("Aberto")
		},{
			value: RisksSituation.FINALY.toString(),
			label: translate("Finalizado")
		},
		{
			value: RisksSituation.CANCELED.toString(),
			label: translate("Cancelado")
		},
		{
			value: RisksSituation.ALL.toString(),
			label: translate("Todos")
		}
	];

	const returnObjectSituation = (value: any) => {
		if(typeof value === "object"){
			return value;
		}
		if(typeof value === "string"){
			const obj = situationOptions.find((option) => option.value === value);
			if(!obj)
				return "";

			return obj;
		}
		return "";
	};

	const filterIsActive = showFilters || 
		filters.filter || 
		filters.active || 
		filters.dateStart  || 
		filters.dateEnd ||
		filters.rangeStart ||  
		filters.rangeEnd|| 
		filters.ria001Id ||  
		(filters.situation && (filters.situation !== RisksSituation.ALL.toString())) || 
		filters.process || 
		filters.projects ||  
		filters.responsable || 
		filters.classification;

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				<RCCGrid
					container
					item
				>
			  	{/* Header -> Results + Buttons */}
					<PageTitle
						title={translate("Matriz de Riscos")}
						icon={() => <AppIcons name="risks" />}
					>
						<RCCTooltip
							text={layoutList === "card" ? translate("Alterar para lista") : translate("Alterar para cards")}
							color="grey"
						>
							<RCCButton
								icon={{
									name: layoutList === "card" ? "list" : "table", 
									color: "#FFF", 
									size: 16
								}}
								color="grey"
								layout="isRounded"
								size="md"
								onClick={() => {
									if(layoutList === "card"){
										setLayoutList("list");
										setLocalStorage(`RisksScreenLayoutList-${user.efa005Id}`, "list");
										return;
									}
									setLocalStorage(`RisksScreenLayoutList-${user.efa005Id}`, "card");
									setLayoutList("card");
								}}
							/>
						</RCCTooltip>
						<RCCTooltip
							text={translate("Filtros")}
							color={filterIsActive? "blue" : "grey" 
	
							}
						>
							<RCCButton
								icon={{name: "filter", color: "#FFF", size: 16}}
								color={filterIsActive ? "blue" : "grey" 
		
								}
								layout="isRounded"
								size="md"
								onClick={() => setShowFilters((prevState) => !prevState)}
							/>
						</RCCTooltip>

					</PageTitle>

					{/* Filters */}
					<FiltersContainer
						onSubmitFilters={handleSubmitFilters(submitFilters)}
						showFilters={showFilters}
						resetFilters={() => resetFilters({ 
							filter: "", 
							dateStart: null,
							dateEnd : null, 
							rangeStart: "",
							rangeEnd: "", 
							ria001Id: "", 
							situation: {
								value: RisksSituation.ALL.toString(),
								label: translate("Todos")
							}, 
							classification: {value : "", label: ""},
							process: {value : "", label: ""}, 
							responsable: {value : "", label: ""},
							projects: {value : "", label: ""} 
						})}
					>
						<RCCGrid
							container
							item
							spacing={2}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={filters.filter}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<LovRiskClassification 
									name="classification"
									control={controlFilters}
									label={translate("Classificação")}
									isClearable
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<LovUsers 
									control={controlFilters}
									name="responsable"
									label={translate("Responsável")}
									defaultValue={ watchFilters("filters.responsable")  || ""}
									isClearable
								/>	
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<DateTimePickerRange
									control={controlFilters}
									nameInit = "dateStart"
									nameEnd = "dateEnd"
									label = {translate("Data")}
									labelSeparator = {translate("até")}
									type = "datePicker"
									defaultValueInit ={filters.dateStart}
									defaultValueEnd ={filters.dateEnd}
									setValue={setValueFilters}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<Select 
									control={controlFilters}
									name="situation" 
									options={situationOptions} 
									label={translate("Situação")}
									defaultValue={filters.situation ? returnObjectSituation(filters.situation) : {
										value: RisksSituation.OPEN.toString(),
										label: translate("Aberto")
									}}
									submit="value and label"
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<LovProcess
									control={controlFilters}
									name="process" 
									label={translate("Processo")}
									defaultValue={filters.process || ""}
									isClearable
									isCreatable={false}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								
								<LovProjects 
									name="projects"
									control={controlFilters}
									label={translate("Projetos")}	
									defaultValue={filters.projects || ""}
									isClearable
								/>
							</RCCGrid>
						</RCCGrid>
					</FiltersContainer>
				</RCCGrid>

				{/* Matriz e listagem */}
				<>
					<RCCGrid
						item
						container
					>
						{Matrix}
					</RCCGrid>
					
					<RCCGrid
						item
						container
						style={{ margin: "1rem 0" }}
					>
						{selected && (
							<>
								<RCCGrid
									item
									container
									style={{ margin: "1rem 0", fontSize: "0.83rem" }}
								>
									<Title color="purple" size="sm" styleInline="width: 100%" >
										<RCCGrid
											item
											container
											justifyContent="space-between"
										>
											<div>{translate("Riscos")} </div>
											<div> 
										  		{selectedProbability &&  selectedImpact && selectedQuadrant && (
													<>
														<span style={{ marginRight: "1rem"}}><b>{translate("Probabilidade")}:</b> {probabilityDictonary.find((elemt) =>  elemt.value === selectedProbability)?.label}</span>
														<span style={{ marginRight: "1rem" }}><b>{translate("Impacto")}:</b> {impactDictonary.find((elemt) =>  elemt.value === selectedImpact)?.label}</span>
														<span><b>{translate("Tipo")}:</b> {selectedQuadrant === CheckQuadrant.THREAT ? translate("Ameaças") : translate("Oportunidades")}</span>
													</>
												)}		
											</div>
										</RCCGrid>	
									</Title>
									<hr />	
								</RCCGrid>	
								{listFiltered.length > 0 ? (
									<ListOfRisks 
										data={listFiltered}
										layoutList={layoutList}
										onCallbackDelete={onCallbackDelete}
										onCallbackUpdate={onCallbackUpdate}
									/>	
								): (
									<RCCAlert 
										title={translate("Atenção")}	
										description={translate("Não foram encontrados resultados para esta pesquisa")}
									/>
								)}
								
							</>
						)}
							
					</RCCGrid>
				</>
						
			</RCCGrid>
		</>
	);

};

export default RisksMatrix;
